import type {
  As,
  BoxProps} from '@chakra-ui/react'
import {
  Badge,
  Box,
  Button,
  Link as ChakraLink,
  createStylesContext,
  Flex,
  Heading,
  Text,
  useMultiStyleConfig
} from '@chakra-ui/react'
import { Ellipsis } from 'BrokerTsLib/src/components'
import type { AppInfoMessage} from 'BrokerTsLib/src/dtos'
import { AppInfoMessageImportanceTypes } from 'BrokerTsLib/src/dtos'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { useAuth } from '../../context/AuthContext'
import { getNewsItemTypeColor } from '../../data/news'
import { useApp } from '../../hooks/useApp'
import type { variants } from '../../theme/components/NewsItem'
import { formatDate } from '../../utils/formatDate'
import { UnreadDot } from './UnreadDot'

interface NewsItemProps extends AppInfoMessage {
  variant?: keyof typeof variants
  showReadButton?: boolean
  showMoreButton?: boolean
  tagLabel: string | undefined
  maxLines: number
}

export const GetAppInfoMessagePriorityText = (type: AppInfoMessageImportanceTypes) => {
  switch (type) {
    case AppInfoMessageImportanceTypes.Standard:
      return 'Informace'

    case AppInfoMessageImportanceTypes.High:
      return 'Prioritní'

    case AppInfoMessageImportanceTypes.Unavailability:
      return 'Nedostupnost'

    default:
      throw Error('Invalid app info message type')
  }
}

export const NewsItemTypeBadge = ({ priority }: { priority: AppInfoMessage['messageImportance'] }) => {
  const colorScheme = getNewsItemTypeColor(priority)

  return <Badge colorScheme={colorScheme}>{GetAppInfoMessagePriorityText(priority)}</Badge>
}

const NewsItemMetadata = ({ children, ...rest }: { children: React.ReactNode } & BoxProps) => {
  const styles = useStyles()

  return (
    <Box sx={styles.metadata} {...rest}>
      {children}
    </Box>
  )
}

const NewsItemTagDateWrapper = ({ children, ...rest }: { children: React.ReactNode }) => {
  const styles = useStyles()

  return (
    <Box sx={{ ...styles.tagDateWrapper }} {...rest}>
      {children}
    </Box>
  )
}

const NewsItemDateDotWrapper = ({ children, ...rest }: { children: React.ReactNode }) => {
  return <Flex sx={{ alignItems: 'center', gap: 2 }}>{children}</Flex>
}

const NewsItemTitle = ({ children, as, ...rest }: { children: React.ReactNode; as: As }) => {
  const styles = useStyles()

  return (
    <Heading sx={styles.title} {...rest}>
      {children}
    </Heading>
  )
}

const NewsItemContent = ({ children, ...rest }: { children: React.ReactNode }) => {
  const styles = useStyles()

  return (
    <Box sx={styles.textContent} {...rest}>
      {children}
    </Box>
  )
}

const [StylesProvider, useStyles] = createStylesContext('NewsItem')

export const NewsItem = ({
  id,
  messageHeader: header,
  messageImportance: importance,
  messageDate: date,
  read = false,
  messageText: text,
  variant,
  showReadButton = false,
  showMoreButton = false,
  messageShow: show,
  tagLabel,
  maxLines,
  ...rest
}: NewsItemProps) => {
  const [unreadLocal, setUnreadLocal] = useState<boolean>(!read)
  const colorScheme = getNewsItemTypeColor(importance)
  const styles = useMultiStyleConfig('NewsItem', { variant, colorScheme })

  const { app } = useApp()
  const newsItemLink = app ? `/${app}/news/${id}` : `/news/${id}`

  const { setReadMessage } = useAuth() ?? {}

  const [isOverflowing, setIsOverflowing] = useState(false)

  return (
    <Box sx={{
      ...styles.container,
      display: 'flex',
      flexDir: 'column',
      width: '100%'
    }} /*{...rest}*/>
      <StylesProvider value={styles}>

        <ChakraLink as={Link} to={newsItemLink} w='100%'>
          <NewsItemMetadata>
            <NewsItemTagDateWrapper>
              {tagLabel && <Badge colorScheme='blue'>{tagLabel}</Badge>}

              <NewsItemDateDotWrapper>
                {date && (
                  <Text sx={{ color: 'gray.700', fontSize: 'sm' }} as="time">
                    {formatDate({ date })}
                  </Text>
                )}
                {unreadLocal && <UnreadDot />}
              </NewsItemDateDotWrapper>
            </NewsItemTagDateWrapper>

            <NewsItemTitle as="h2">
              <Badge colorScheme={colorScheme}>
                {header}
              </Badge>
            </NewsItemTitle>
          </NewsItemMetadata>
        </ChakraLink>

        {text &&
          <NewsItemContent>
            <Ellipsis onEllipsisActiveChange={setIsOverflowing} noOfLines={maxLines} dangerouslySetInnerHTML={{ __html: text ?? '' }}
              sx={{ width: '100%', px: 2 }}
            />
          </NewsItemContent>
        }

        <Flex
          sx={{
            w: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            alignSelf: 'end',
          }}
        >
          {showReadButton && (
            <Button as={Link} to={newsItemLink} variant="outline" size="sm">
              Detail
            </Button>
          )}
          {showMoreButton && isOverflowing && (
            <Button as={Link} to={newsItemLink} variant="ghost" size="sm">
              Zobrazit více
            </Button>
          )}

          {unreadLocal && (
            <Button type="button" variant="ghost" size="sm" sx={{ marginLeft: 'auto' }}
              onClick={() => {
                setUnreadLocal(false)
                if (setReadMessage) setReadMessage(id)
              }}>
              Označit jako přečtenou
            </Button>
          )}
        </Flex>
      </StylesProvider>
    </Box >
  )
}
